import React, { lazy, Suspense, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useRouteError } from "react-router-dom";
import { useAuth } from "../lib/auth-context";
import { Loader2 } from "lucide-react";
import { AppShell } from "../components/nav/app-shell";
import { NotificationHost } from "../components/ui/notification-host";
import { userSatisfiesRole } from "../lib/utils";
import { roleHome } from "../lib/role-home";
import { can, isStaff, type Permission } from "../lib/permissions";
import { ImpersonationBanner } from "../components/nav/impersonation-banner";
import { ClubAccessBanner } from "../components/nav/club-access-banner";
import { DemoBanner } from "../components/nav/demo-banner";
import { UpdateBanner } from "../components/nav/update-banner";
import { SuperAdminLayout } from "../components/super-admin/layout";
import { useUsageTracking } from "../lib/usage";
import { useTranslation } from "react-i18next";

// Each screen is its own chunk: the entry bundle stays small and a person only
// downloads the screens they actually open. The shell (AppShell, nav,
// notification host) stays eager so navigating never re-mounts the frame.
const LoginPage = lazy(() => import("../pages/login"));
const AuthVerifyPage = lazy(() => import("../pages/auth-verify"));
const CoachDashboard = lazy(() => import("../pages/coach-dashboard"));
const CoachClassesPage = lazy(() => import("../pages/coach-classes"));
const CoachClassPage = lazy(() => import("../pages/coach-class"));
const CoachCalendarPage = lazy(() => import("../pages/coach-calendar"));
const CoachCreateSessionPage = lazy(() => import("../pages/coach-create-session"));
const AdminPlanningPage = lazy(() => import("../pages/admin-planning"));
const AdminPlanningSettingsPage = lazy(() => import("../pages/admin-planning-settings"));
const AdminLocationsPage = lazy(() => import("../pages/admin-locations"));
const AdminClassesPage = lazy(() => import("../pages/admin-classes"));
const AdminClassPage = lazy(() => import("../pages/admin-class"));
const AdminProgramsPage = lazy(() => import("../pages/admin-programs"));
const AdminProgramPage = lazy(() => import("../pages/admin-program"));
const AdminProgramBeltsPage = lazy(() => import("../pages/admin-program-belts"));
const AdminCurriculumItemPage = lazy(() => import("../pages/admin-curriculum-item"));
const AdminClubPage = lazy(() => import("../pages/admin-club"));
const AdminSitePage = lazy(() => import("../pages/admin-site"));
const AdminSiteArticlesPage = lazy(() => import("../pages/admin-site-articles"));
const AdminSiteGalleriesPage = lazy(() => import("../pages/admin-site-galleries"));
const AdminSiteMediaPage = lazy(() => import("../pages/admin-site-media"));
const AdminSiteSettingsPage = lazy(() => import("../pages/admin-site-settings"));
const AdminSiteCoachesPage = lazy(() => import("../pages/admin-site-coaches"));
const AdminSitePartnersPage = lazy(() => import("../pages/admin-site-partners"));
const AdminFeaturesPage = lazy(() => import("../pages/admin-features"));
const AdminPaymentsPage = lazy(() => import("../pages/admin-payments"));
const AdminSitePreviewPage = lazy(() => import("../pages/admin-site-preview"));
const AdminActivitiesPage = lazy(() => import("../pages/admin-activities"));
const AdminBadgesPage = lazy(() => import("../pages/admin-badges"));
const AdminEventsPage = lazy(() => import("../pages/admin-events"));
const AdminEventPage = lazy(() => import("../pages/admin-event"));
const AdminReportsPage = lazy(() => import("../pages/admin-reports"));
const AdminReportSettingsPage = lazy(() => import("../pages/admin-report-settings"));
const AdminRegistrationsPage = lazy(() => import("../pages/admin-registrations"));
const AdminImportsPage = lazy(() => import("../pages/admin-imports"));
const AdminImportStudentsPage = lazy(() => import("../pages/admin-import-students"));
const AdminImportFfjdaPage = lazy(() => import("../pages/admin-import-ffjda"));
const AdminRegistrationSetupPage = lazy(() => import("../pages/admin-registration-setup"));
const PublicRegistrationPage = lazy(() => import("../pages/public-registration"));
const PublicEventPage = lazy(() => import("../pages/public-event"));
const PublicSessionPage = lazy(() => import("../pages/public-session"));
const PublicOfferPage = lazy(() => import("../pages/public-offer"));
const AdminBookingsPage = lazy(() => import("../pages/admin-bookings"));
const BadgeClaimPage = lazy(() => import("../pages/badge-claim"));
const PrivacyPage = lazy(() => import("../pages/legal").then((m) => ({ default: m.PrivacyPage })));
const LegalNoticePage = lazy(() => import("../pages/legal").then((m) => ({ default: m.LegalNoticePage })));
const ProcessorsPage = lazy(() => import("../pages/legal").then((m) => ({ default: m.ProcessorsPage })));
const DpaPage = lazy(() => import("../pages/legal").then((m) => ({ default: m.DpaPage })));
const TeenPrivacyPage = lazy(() => import("../pages/legal").then((m) => ({ default: m.TeenPrivacyPage })));
const StudentDashboard = lazy(() => import("../pages/student-dashboard"));
const AdminDashboardV2 = lazy(() => import("../pages/admin-dashboard-v2"));
const AdminUsersPage = lazy(() => import("../pages/admin-users"));
const AdminStudentsPage = lazy(() => import("../pages/admin-students"));
const AdminAuditLogPage = lazy(() => import("../pages/admin-audit-log"));
const TechniquesPage = lazy(() => import("../pages/techniques"));
const SessionDetailPage = lazy(() => import("../pages/session-detail"));
const TechniqueDetailPage = lazy(() => import("../pages/technique-detail"));
const CurriculumPage = lazy(() => import("../pages/curriculum"));
const StudentHomePage = lazy(() => import("../pages/student-home"));
const StudentBookingsPage = lazy(() => import("../pages/student-bookings"));
const SessionBookingsPage = lazy(() => import("../pages/session-bookings"));
const StudentAttendancePage = lazy(() => import("../pages/student-attendance"));
const StudentCardPage = lazy(() => import("../pages/student-card"));
const TrainingHistoryPage = lazy(() => import("../pages/training-history"));
const MyBadgesPage = lazy(() => import("../pages/my-badges"));
const AccountNotificationsPage = lazy(() => import("../pages/account-notifications"));
const AccountDataPage = lazy(() => import("../pages/account-data"));
const AccountConsentsPage = lazy(() => import("../pages/account-consents"));
const AccountProfilePage = lazy(() => import("../pages/account-profile"));
const FlashcardsPage = lazy(() => import("../pages/flashcards"));
const CoachStudentCurriculumPage = lazy(() => import("../pages/coach-student-curriculum"));
const CoachAttendancePage = lazy(() => import("../pages/coach-attendance"));
const CoachAttendanceStatsPage = lazy(() => import("../pages/coach-attendance-stats"));
const CoachStudentDashboardPage = lazy(() => import("../pages/coach-student-dashboard"));
const SchedulePage = lazy(() => import("../pages/schedule"));
const ImpersonatePage = lazy(() => import("../pages/impersonate"));
const ChooseProfilePage = lazy(() => import("../pages/choose-profile"));
const SuperAdminInboxPage = lazy(() => import("../pages/super-admin/inbox"));
const SuperAdminSignalementsPage = lazy(() => import("../pages/super-admin/signalements"));
const SuperAdminErasureRequestsPage = lazy(() => import("../pages/super-admin/erasure-requests"));
const SuperAdminPdfRequestsPage = lazy(() => import("../pages/super-admin/pdf-requests"));
const SuperAdminClubsPage = lazy(() => import("../pages/super-admin/clubs"));
const SuperAdminClubNewPage = lazy(() => import("../pages/super-admin/club-new"));
const SuperAdminClubPage = lazy(() => import("../pages/super-admin/club"));
const SuperAdminPersonPage = lazy(() => import("../pages/super-admin/person"));
const SuperAdminJournalPage = lazy(() => import("../pages/super-admin/journal"));
const SuperAdminQueuePage = lazy(() => import("../pages/super-admin/queue"));
const SuperAdminUsagePage = lazy(() => import("../pages/super-admin/usage"));
const SuperAdminMediaPage = lazy(() => import("../pages/super-admin/media"));
const SuperAdminMediaProgramPage = lazy(() => import("../pages/super-admin/media-program"));
const SuperAdminAppearancePage = lazy(() => import("../pages/super-admin/appearance"));
const SuperAdminSiteHostPage = lazy(() => import("../pages/super-admin/site-host"));
const SuperAdminBadgesPage = lazy(() => import("../pages/super-admin/badges"));
const SuperAdminSiteReadinessPage = lazy(() => import("../pages/super-admin/site-readiness"));
const SuperAdminSetupTokensPage = lazy(() => import("../pages/super-admin/setup-tokens"));

/** Shown while a page chunk downloads; the app frame around it is already up. */
function PageSpinner() {
  return (
    <div className="flex justify-center py-16">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

/** Same, for routes that have no frame yet (login, public registration…). */
function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

/** A page that renders outside ProtectedRoute/PublicRoute (auth verify, impersonate, registration link). */
function StandaloneRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<FullScreenSpinner />}>{children}</Suspense>;
}

// Hard redirect done in an effect (never during render).
function Redirect({ to }: { to: string }) {
  useEffect(() => { window.location.href = to; }, [to]);
  return null;
}

/** The coach workspace lived under /sensei: keep old bookmarks and installed-app links working. */
function LegacyCoachRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={pathname.replace(/^\/sensei/, "/coach") + search + hash} replace />;
}

function RouteError() {
  const error = useRouteError() as any;
  const { t } = useTranslation("common");
  const message = error?.statusText || error?.message || t("errors.unknown");

  // React Router renders this element instead of letting the error bubble up, so
  // <Sentry.ErrorBoundary> above never sees it. Report it here or it stays invisible.
  useEffect(() => {
    Sentry.captureException(error, { tags: { boundary: "react-router" } });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="max-w-md space-y-4 rounded-lg border bg-card p-6 text-center shadow-sm">
        <h1 className="text-xl font-semibold">{t("errors.unexpected")}</h1>
        <p className="text-sm text-muted-foreground break-words">{message}</p>
        <div className="flex justify-center gap-2">
          <button className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground" onClick={() => window.location.reload()}>
            {t("errors.reload")}
          </button>
          <a className="rounded-md border px-4 py-2 text-sm" href="/">{t("errors.home")}</a>
        </div>
      </div>
    </div>
  );
}

// Layout wrapper for authenticated pages
function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  // Reports the screen the person is on; the "last activity" the admin lists show.
  useUsageTracking();

  return (
    <div className="min-h-screen bg-background">
      <ImpersonationBanner />
      <ClubAccessBanner />
      <DemoBanner />
      <UpdateBanner />
      <NotificationHost />
      <AppShell>
        <main className="container mx-auto px-4 pt-4 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:pt-6 md:py-8">
          {/* Keyed by person: a parent switching child in place remounts the page, so it refetches. */}
          {/* Only the page suspends here: the header, nav and notifications stay put while its chunk loads. */}
          <Suspense key={user?.id} fallback={<PageSpinner />}>{children}</Suspense>
        </main>
      </AppShell>
    </div>
  );
}

// Protected Route Component
// `permission` and `staff` say what the screen needs from the club (see lib/permissions.ts);
// `requiredRole` only separates who the person is (member, platform super admin).
function ProtectedRoute({ children, requiredRole, permission, staff, bare }: { children: React.ReactNode; requiredRole?: string; permission?: Permission; staff?: boolean; bare?: boolean }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullScreenSpinner />;
  }

  if (!user) return <Redirect to="/login" />;

  if (
    (requiredRole && !userSatisfiesRole(user, requiredRole))
    || (permission && !can(user, permission))
    || (staff && !isStaff(user))
  ) {
    return <Redirect to={roleHome(user) ?? "/login"} />;
  }

  // "Bare" screens (super-admin, choose-profile) bring their own frame, so they
  // suspend over a full-screen spinner; the rest keep the app shell visible.
  return bare
    ? <Suspense fallback={<FullScreenSpinner />}>{children}</Suspense>
    : <AuthenticatedLayout>{children}</AuthenticatedLayout>;
}

// Public Route Component (redirects if already authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user) {
    const home = roleHome(user);
    // Signed in but no role in the active club: never render nothing.
    if (home) return <Redirect to={home} />;
    return <Suspense fallback={<FullScreenSpinner />}>{children}</Suspense>;
  }

  return <Suspense fallback={<FullScreenSpinner />}>{children}</Suspense>;
}

// Route definitions
const routes = [
  {
    path: "/",
    element: <PublicRoute><LoginPage /></PublicRoute>,
  },
  {
    path: "/login",
    element: <PublicRoute><LoginPage /></PublicRoute>,
  },
  {
    path: "/auth/verify",
    element: <StandaloneRoute><AuthVerifyPage /></StandaloneRoute>,
  },
  {
    path: "/impersonate",
    element: <StandaloneRoute><ImpersonatePage /></StandaloneRoute>,
  },
  {
    // The club's online registration form: open to anyone with the link, signed in or not.
    path: "/inscription/:slug",
    element: <StandaloneRoute><PublicRegistrationPage /></StandaloneRoute>,
  },
  // The legal texts: public, readable signed in or not (GDPR transparency).
  { path: "/confidentialite", element: <StandaloneRoute><PrivacyPage /></StandaloneRoute> },
  { path: "/mentions-legales", element: <StandaloneRoute><LegalNoticePage /></StandaloneRoute> },
  { path: "/sous-traitants", element: <StandaloneRoute><ProcessorsPage /></StandaloneRoute> },
  { path: "/dpa", element: <StandaloneRoute><DpaPage /></StandaloneRoute> },
  { path: "/ma-vie-privee", element: <StandaloneRoute><TeenPrivacyPage /></StandaloneRoute> },
  {
    // A flyer's address: the public event page, open to anyone with the link.
    path: "/evenements/:slug",
    element: <StandaloneRoute><PublicEventPage /></StandaloneRoute>,
  },
  {
    // An offer's public dates (a trial dive, a discovery lesson): the address a club links from its site.
    path: "/reserver/:token",
    element: <StandaloneRoute><PublicOfferPage /></StandaloneRoute>,
  },
  {
    // A session the club opened to the public (trial dive, discovery lesson): book without an account.
    path: "/seance/:token",
    element: <StandaloneRoute><PublicSessionPage /></StandaloneRoute>,
  },
  {
    // A badge claim link, mailed to somebody who has no account yet.
    path: "/badges/reclamer",
    element: <StandaloneRoute><BadgeClaimPage /></StandaloneRoute>,
  },
  {
    // A parent picks which child to open the app as; no club frame of their own.
    path: "/choose-profile",
    element: (
      <ProtectedRoute bare>
        <ChooseProfilePage />
      </ProtectedRoute>
    ),
  },
  {
    // Platform operators: their own frame, no club. The guard here is only
    // for the interface; every /api/superadmin route enforces the role itself.
    path: "/super-admin",
    element: (
      <ProtectedRoute requiredRole="ROLE_SUPER_ADMIN" bare>
        <SuperAdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <SuperAdminInboxPage /> },
      { path: "signalements", element: <SuperAdminSignalementsPage /> },
      { path: "erasures", element: <SuperAdminErasureRequestsPage /> },
      { path: "pdf-requests", element: <SuperAdminPdfRequestsPage /> },
      { path: "clubs", element: <SuperAdminClubsPage /> },
      { path: "clubs/new", element: <SuperAdminClubNewPage /> },
      { path: "clubs/:id", element: <SuperAdminClubPage /> },
      { path: "people/:id", element: <SuperAdminPersonPage /> },
      { path: "journal", element: <SuperAdminJournalPage /> },
      { path: "queue", element: <SuperAdminQueuePage /> },
      { path: "setup-tokens", element: <SuperAdminSetupTokensPage /> },
      // Former address of the journal.
      { path: "activity", element: <Navigate to="/super-admin/journal" replace /> },
      { path: "usage", element: <SuperAdminUsagePage /> },
      { path: "appearance", element: <SuperAdminAppearancePage /> },
      { path: "site-host", element: <SuperAdminSiteHostPage /> },
      { path: "badges", element: <SuperAdminBadgesPage /> },
      { path: "site-readiness", element: <SuperAdminSiteReadinessPage /> },
      { path: "media", element: <SuperAdminMediaPage /> },
      { path: "media/:key", element: <SuperAdminMediaProgramPage /> },
    ],
  },
  {
    // The old EasyAdmin address: bookmarks and mails still point here.
    path: "/superadmin/*",
    element: <Redirect to="/super-admin" />,
  },
  {
    path: "/sensei/*",
    element: <LegacyCoachRedirect />,
  },
  {
    path: "/coach",
    element: (
      <ProtectedRoute staff>
        <Navigate to="/coach/classes" replace />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coach/classes",
    element: (
      <ProtectedRoute permission="classes.manage_own">
        <CoachClassesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coach/classes/:id",
    element: (
      <ProtectedRoute permission="classes.manage_own">
        <CoachClassPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coach/students/:studentId",
    element: (
      <ProtectedRoute permission="curriculum.manage">
        <CoachStudentCurriculumPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coach/presences",
    element: (
      <ProtectedRoute permission="attendance.take">
        <CoachAttendancePage />
      </ProtectedRoute>
    ),
  },
  {
    // Per-session detail of the dashboard's weekly chart; lives under it so the menu highlights "Dashboard".
    path: "/coach/tableau-de-bord/presences",
    element: (
      <ProtectedRoute permission="attendance.take">
        <CoachAttendanceStatsPage />
      </ProtectedRoute>
    ),
  },
  {
    // Former address, which the menu mistook for the roll call ("/coach/presences").
    path: "/coach/presences/stats",
    element: <Navigate to="/coach/tableau-de-bord/presences" replace />,
  },
  {
    path: "/coach/tableau-de-bord",
    element: (
      <ProtectedRoute staff>
        <CoachStudentDashboardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coach/sessions",
    element: (
      <ProtectedRoute permission="classes.manage_own">
        <CoachDashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coach/calendar",
    element: (
      <ProtectedRoute staff>
        <CoachCalendarPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coach/horaires",
    element: (
      <ProtectedRoute staff>
        <SchedulePage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coach/create-session",
    element: (
      <ProtectedRoute permission="classes.manage_own">
        <CoachCreateSessionPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/student",
    element: (
      <ProtectedRoute requiredRole="ROLE_STUDENT">
        <StudentHomePage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/student/programme",
    element: (
      <ProtectedRoute requiredRole="ROLE_STUDENT">
        <CurriculumPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/student/presences",
    element: (
      <ProtectedRoute requiredRole="ROLE_STUDENT">
        <StudentAttendancePage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/compte/notifications",
    element: (
      <ProtectedRoute>
        <AccountNotificationsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/compte/profil",
    element: (
      <ProtectedRoute>
        <AccountProfilePage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/compte/consentements",
    element: (
      <ProtectedRoute>
        <AccountConsentsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/compte/donnees",
    element: (
      <ProtectedRoute>
        <AccountDataPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/historique",
    element: (
      <ProtectedRoute>
        <TrainingHistoryPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/mes-badges",
    element: (
      <ProtectedRoute>
        <MyBadgesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/student/carte",
    element: (
      <ProtectedRoute requiredRole="ROLE_STUDENT">
        <StudentCardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/student/flashcards",
    element: (
      <ProtectedRoute requiredRole="ROLE_STUDENT">
        <FlashcardsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/student/reservations",
    element: (
      <ProtectedRoute requiredRole="ROLE_STUDENT">
        <StudentBookingsPage />
      </ProtectedRoute>
    ),
  },
  {
    // The people on one bookable session: the staff who run it and the planning managers.
    path: "/seances/:id",
    element: (
      <ProtectedRoute permission="attendance.take">
        <SessionBookingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/student/horaires",
    element: (
      <ProtectedRoute requiredRole="ROLE_STUDENT">
        <SchedulePage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/student/sessions",
    element: (
      <ProtectedRoute requiredRole="ROLE_STUDENT">
        <StudentDashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin",
    element: (
      <ProtectedRoute permission="reports.view">
        <AdminDashboardV2 />
      </ProtectedRoute>
    ),
  },
  {
    // The dashboard's former trial address: bookmarks still point here.
    path: "/admin/v2",
    element: <Navigate to="/admin" replace />,
  },
  {
    path: "/admin/users",
    element: (
      <ProtectedRoute permission="members.view">
        <AdminUsersPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/students",
    element: (
      <ProtectedRoute permission="members.view">
        <AdminStudentsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/students/:studentId",
    element: (
      <ProtectedRoute permission="members.view">
        <CoachStudentCurriculumPage backTo="/admin/students" backLabelKey="coachStudentCurriculum.retourAuxEleves" adminView />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/audit-log",
    element: (
      <ProtectedRoute permission="audit.view">
        <AdminAuditLogPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/planning",
    element: (
      <ProtectedRoute permission="planning.view">
        <AdminPlanningPage />
      </ProtectedRoute>
    ),
  },
  {
    // Everything members book session by session (dives, private lessons, runs).
    path: "/admin/reservations",
    element: (
      <ProtectedRoute permission="planning.manage">
        <AdminBookingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/planning/reglages",
    element: (
      <ProtectedRoute permission="planning.manage">
        <AdminPlanningSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    // Same roll call as the coach's: an admin runs it from their own workspace.
    path: "/admin/presences",
    element: (
      <ProtectedRoute permission="attendance.correct">
        <CoachAttendancePage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/lieux",
    element: (
      <ProtectedRoute permission="planning.manage">
        <AdminLocationsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/classes",
    element: (
      <ProtectedRoute permission="planning.manage">
        <AdminClassesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/classes/:id",
    element: (
      <ProtectedRoute permission="planning.manage">
        <AdminClassPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/programs",
    element: (
      <ProtectedRoute permission="curriculum.manage">
        <AdminProgramsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/programs/:id",
    element: (
      <ProtectedRoute permission="curriculum.manage">
        <AdminProgramPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/programs/:id/belts",
    element: (
      <ProtectedRoute permission="grades.award">
        <AdminProgramBeltsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/programs/:id/items/:itemId",
    element: (
      <ProtectedRoute permission="curriculum.manage">
        <AdminCurriculumItemPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coach/programs",
    element: (
      <ProtectedRoute permission="curriculum.manage">
        <AdminProgramsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coach/programs/:id",
    element: (
      <ProtectedRoute permission="curriculum.manage">
        <AdminProgramPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coach/programs/:id/items/:itemId",
    element: (
      <ProtectedRoute permission="curriculum.manage">
        <AdminCurriculumItemPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/site",
    element: (
      <ProtectedRoute permission="club.settings" bare>
        <AdminSitePage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/site/coaches",
    element: (
      <ProtectedRoute permission="club.settings">
        <AdminSiteCoachesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/site/partners",
    element: (
      <ProtectedRoute permission="club.settings">
        <AdminSitePartnersPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/site/settings/:tab?",
    element: (
      <ProtectedRoute permission="club.settings">
        <AdminSiteSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/site/articles",
    element: (
      <ProtectedRoute permission="club.settings">
        <AdminSiteArticlesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/site/galleries",
    element: (
      <ProtectedRoute permission="club.settings">
        <AdminSiteGalleriesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/site/preview",
    element: (
      <ProtectedRoute permission="club.settings">
        <AdminSitePreviewPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/site/media",
    element: (
      <ProtectedRoute permission="club.settings">
        <AdminSiteMediaPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/paiements",
    element: (
      <ProtectedRoute permission="club.settings">
        <AdminPaymentsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/fonctionnalites",
    element: (
      <ProtectedRoute permission="club.settings">
        <AdminFeaturesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/activites",
    element: (
      <ProtectedRoute permission="club.settings">
        <AdminActivitiesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/badges",
    element: (
      <ProtectedRoute permission="badges.manage">
        <AdminBadgesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/events",
    element: (
      <ProtectedRoute permission="events.manage">
        <AdminEventsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/events/:id",
    element: (
      <ProtectedRoute permission="events.manage">
        <AdminEventPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/club",
    element: (
      <ProtectedRoute permission="club.settings">
        <AdminClubPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/inscriptions",
    element: (
      <ProtectedRoute permission="registrations.review">
        <AdminRegistrationsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/imports",
    element: (
      <ProtectedRoute permission="members.import">
        <AdminImportsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/imports/students",
    element: (
      <ProtectedRoute permission="members.import">
        <AdminImportStudentsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/imports/ffjda",
    element: (
      <ProtectedRoute permission="members.import">
        <AdminImportFfjdaPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/inscriptions/reglages",
    element: (
      <ProtectedRoute permission="registrations.configure">
        <AdminRegistrationSetupPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/rapports",
    element: (
      <ProtectedRoute permission="reports.view">
        <AdminReportsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/rapports/reglages",
    element: (
      <ProtectedRoute permission="reports.view">
        <AdminReportSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/techniques",
    element: (
      <ProtectedRoute>
        <TechniquesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/session/:id",
    element: (
      <ProtectedRoute>
        <SessionDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/technique/:id",
    element: (
      <ProtectedRoute>
        <TechniqueDetailPage />
      </ProtectedRoute>
    ),
  },
  // Catch all route for 404
  {
    path: "*",
    element: <div>Page Not Found</div>,
  },
];

const router = createBrowserRouter(routes.map((r) => ({ ...r, errorElement: <RouteError /> })));

export default function AppRouter() {
  // Safety net: everything above has its own boundary, but a chunk fetched
  // outside them (a nested layout, the super-admin frame) must never blank the page.
  return (
    <Suspense fallback={<FullScreenSpinner />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}

